import { formatCurrency } from "@helpers/funciones";
import { LoanApplicationOfferResponse } from "@interfaces/MarketPlace";

import { useEffect, useState } from "react";
import { Grid, Box, useTheme, Typography, Button, Divider } from "@mui/material";
import icon_partner from "@assets/img/market_place/icon_partner.png";

interface CreditOfferCardProps {
  offer: LoanApplicationOfferResponse;
  offers_length: number;
  showButton: boolean;
  onSelectOffer?: () => void;
  index?: number;
}

const fontSize = "14px";

const CreditOfferCard: React.FC<CreditOfferCardProps> = ({ offer, showButton, onSelectOffer }) => {
  const theme = useTheme();
  const companyName = offer.financial_company?.name || "Financiera";
  const hasLogo = Boolean(offer.financial_company?.logo);
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    setLogoError(false);
  }, [offer.financial_company?.logo]);

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ flex: "1 1 auto" }}>
        <Box display="flex" justifyContent="center" alignItems="center">
          <div
            style={{
              width: "150px",
              height: "80px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "transparent",
            }}
          >
            {hasLogo && !logoError ? (
              <img
                src={`${import.meta.env.VITE_URL_STATIC_RESOURCES}/${offer.financial_company?.logo}`}
                alt={companyName}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
                onError={() => {
                  setLogoError(true);
                }}
              />
            ) : (
              <Typography
                variant={"h3"}
                fontWeight={500}
                color={theme?.palette?.primary?.dark}
                textAlign={"center"}
                sx={{ px: 1 }}
              >
                {companyName}
              </Typography>
            )}
          </div>
        </Box>

        <Box mt={2} mb={2}>
          <Typography variant={"body1"} fontWeight={500} color={theme?.palette?.primary?.dark} textAlign={"center"}>
            Crédito simple
          </Typography>
          {offer.financial_company.is_partner && (
            <Box display="flex" justifyContent="center" mt={1}>
              <img
                src={icon_partner}
                alt="Partner Logo"
                style={{
                  width: "100%",
                  maxWidth: "210px",
                }}
              />
            </Box>
          )}
        </Box>

        <Grid container mt={4}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="stretch" width="100%">
              <Box flex={1}>
                <Typography sx={{ mb: 1 }} variant={"body1"} fontWeight={500} color={"#6387D4"} textAlign={"center"}>
                  Monto Máximo
                </Typography>
                <Typography
                  fontSize={fontSize}
                  fontWeight={400}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  {formatCurrency(offer.details?.maxAuthorizedAmount || 0)} <span style={{ fontSize: 10 }}>MXN</span>
                </Typography>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ mr: "-2px", border: "1px solid #FF9E1B" }} />

              <Box flex={1}>
                <Typography sx={{ mb: 1 }} variant={"body1"} fontWeight={500} color={"#6387D4"} textAlign={"center"}>
                  Tasa Anual
                </Typography>
                <Typography
                  fontSize={fontSize}
                  fontWeight={400}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  {offer.details?.interestRate || 0}% <span style={{ fontSize: 14 }}>anual</span>
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Grid container mt={4}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="stretch" width="100%">
              <Box flex={1}>
                <Typography sx={{ mb: 1 }} variant={"body1"} fontWeight={500} color={"#6387D4"} textAlign={"center"}>
                  Tiempo <br /> Formalización
                </Typography>
                <Typography
                  fontSize={fontSize}
                  fontWeight={400}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  {offer.details?.formalizationTime || 0} días
                </Typography>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ mr: "-2px", border: "1px solid #FF9E1B" }} />

              <Box flex={1}>
                <Typography sx={{ mb: 1 }} variant={"body1"} fontWeight={500} color={"#6387D4"} textAlign={"center"}>
                  Garantía
                </Typography>
                <Typography
                  fontSize={fontSize}
                  fontWeight={400}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  {offer.details?.withGuarantee ? offer.details?.guaranteeDetails : "N/A"}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Grid container mt={4}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="stretch" width="100%">
              <Box flex={1}>
                <Typography sx={{ mb: 1 }} variant={"body1"} fontWeight={500} color={"#6387D4"} textAlign={"center"}>
                  Comisiones
                </Typography>
                <Typography
                  fontSize={fontSize}
                  fontWeight={400}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  {offer.details?.openingFee || 0}%
                </Typography>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ mr: "-2px", border: "1px solid #FF9E1B" }} />

              <Box flex={1}>
                <Typography sx={{ mb: 1 }} variant={"body1"} fontWeight={500} color={"#6387D4"} textAlign={"center"}>
                  Plazo
                </Typography>
                <Typography
                  fontSize={fontSize}
                  fontWeight={400}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  {offer.details?.minAuthorizedPeriod || 0} a {offer.details?.maxAuthorizedPeriod || 0} meses
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Box mb={2} mt={2} mx={2}>
          <Typography sx={{ mb: 1 }} variant={"body1"} fontWeight={500} color={"#6387D4"} fontSize={14}>
            Preoferta sujeta a
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, color: theme?.palette?.primary?.dark }}>
            {offer.financial_company.is_partner ? (
              <>
                <Box component="li" sx={{ typography: "body2" }}>
                  Formalización digital mediante la plataforma {offer.financial_company.name} - Lendia.
                </Box>
                {offer.details?.specialConditions && (
                  <Box component="li" sx={{ typography: "body2" }}>
                    {offer.details.specialConditions}
                  </Box>
                )}
              </>
            ) : (
              <>
                <Box component="li" sx={{ typography: "body2" }}>
                  Formalización a través de la financiera.
                </Box>
                {offer.details?.specialConditions && (
                  <Box component="li" sx={{ typography: "body2" }}>
                    {offer.details.specialConditions}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      {showButton && (
        <Box display="flex" justifyContent="center" mt="auto" mb={4}>
          <Button variant="yellow" onClick={onSelectOffer} sx={{ width: "100%", maxWidth: "250px", padding: "5px" }}>
            Elegir preoferta
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CreditOfferCard;
